// src/pages/ContactsPage.tsx

import { useMemo, useState, useEffect, useRef } from 'react';
import {
  useGetContactsQuery,
  useCreateContactMutation,
  useDeleteContactMutation,
} from '../api/contactsApi';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useCircleRealtime } from '../hooks/useCircleRealtime';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorState from '../components/common/ErrorState';
import Button from '../components/common/Button';
import ListPager from '../components/common/ListPager';
import ContactDetailSlideOver from '../components/contacts/ContactDetailSlideOver';
import ContactQuickActions from '../components/contacts/ContactQuickActions';
import CompanyMultiPicker, { type CompanyChoice } from '../components/contacts/CompanyMultiPicker';
import {
  LinkDetailsSection,
  compactLinkDetails,
} from '../components/contacts/AffiliationRelationship';
import { relationshipFieldErrors, type CreateLinkDetails } from '../api/contactCompanyApi';
import { DeleteConfirmDialog } from '../components/ui/ConfirmDialog';
import { useContactPermissions } from '../hooks/useContactCompanyPermissions';
import { capabilityDeniedMessage } from '../utils/capabilityError';
import {
  ContactBulkAddToCircle,
  ContactCircleFilter,
} from '../components/circles/ContactCircleActions';
import type { Contact } from '../types/api';

const CONTACT_TYPES = ['lead', 'client', 'vendor', 'partner', 'team', 'other'];

/** Rows per page — the server's default for GET /api/contacts. */
const CONTACTS_PAGE_SIZE = 100;

interface ContactsPageProps {
  /** Open this contact on arrival — from a `/contacts?contactId=` link. */
  initialContactId?: string | null;
  /** Called when the open contact is closed, so the caller can clear the link. */
  onContactClosed?: () => void;
}

export default function ContactsPage({ initialContactId, onContactClosed }: ContactsPageProps = {}) {
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [tagsInput, setTagsInput] = useState('');
  const [debouncedTags, setDebouncedTags] = useState('');
  const [circleFilterId, setCircleFilterId] = useState<string | null>(null);
  const hasActiveFilters = !!(searchQuery.trim() || typeFilter || tagsInput.trim() || circleFilterId);
  const clearFilters = () => {
    setSearchQuery('');
    setTypeFilter('');
    setTagsInput('');
    setCircleFilterId(null);
  };
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkCircleModal, setShowBulkCircleModal] = useState(false);
  const [circleActionBanner, setCircleActionBanner] = useState<{
    kind: 'success' | 'error';
    message: string;
  } | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // The Add Contact form is a hand-rolled overlay rather than the shared
  // Modal, so it traps focus itself.
  const createDialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(createDialogRef, showCreateModal);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(
    initialContactId ?? null,
  );
  // A later link to a different contact (same mounted page) opens that one.
  useEffect(() => {
    if (initialContactId) setSelectedContactId(initialContactId);
  }, [initialContactId]);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  /**
   * Why the last delete was refused. Kept so the dialog can SAY so rather
   * than closing as though it had worked — the row would still be sitting
   * there afterwards, which is a confusing way to learn you lack permission.
   */
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Keep circle-list caches fresh for the filter + bulk-add modal.
  useCircleRealtime({ workspaceId });

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Debounce tags input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTags(tagsInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [tagsInput]);

  // Paging. Any change to what is being listed starts again at page one.
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    setOffset(0);
  }, [workspaceId, debouncedQuery, typeFilter, debouncedTags, circleFilterId]);

  // Polling + focus/reconnect refetch — guaranteed near-realtime refresh
  // for cross-app contact changes (Outbound → CRM webhook syncs land via
  // PUT /api/internal/contacts/by-external-source, which fires the
  // realtime emitter; this polling is the bulletproof fallback for the
  // case when the WebSocket isn't connected — e.g. local dev tab opened
  // before the /ws proxy was set, mobile tabs that drop the WS in the
  // background, transient backend restarts).
  const {
    data: contactsResponse,
    isLoading,
    error,
    refetch,
  } = useGetContactsQuery(
    {
      workspaceId,
      q: debouncedQuery || undefined,
      type: typeFilter || undefined,
      tags: debouncedTags || undefined,
      // Circle membership is filtered SERVER-side, so the page and the total
      // both describe the Circle's members — not the first page intersected
      // with the member list, which is what the client-side filter did.
      circleId: circleFilterId || undefined,
      limit: CONTACTS_PAGE_SIZE,
      offset,
    },
    {
      skip: !workspaceId,
      pollingInterval: 10000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  const contacts = useMemo(() => contactsResponse?.contacts ?? [], [contactsResponse]);
  const totalContacts = contactsResponse?.pagination?.total ?? contacts.length;

  // Deleting the last rows of the last page (here or elsewhere, via polling)
  // would leave the reader on an empty page past the end: step back.
  useEffect(() => {
    if (offset > 0 && totalContacts > 0 && offset >= totalContacts) {
      setOffset(Math.floor((totalContacts - 1) / CONTACTS_PAGE_SIZE) * CONTACTS_PAGE_SIZE);
    }
  }, [offset, totalContacts]);

  const contactIdOnPage = useMemo(
    () => new Set((contacts ?? []).map((c) => c.id)),
    [contacts],
  );
  const displayedSelectedIds = useMemo(
    () => Array.from(selectedIds).filter((id) => contactIdOnPage.has(id)),
    [selectedIds, contactIdOnPage],
  );
  const allDisplayedChecked =
    (contacts?.length ?? 0) > 0 &&
    displayedSelectedIds.length === contacts.length;

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllDisplayed = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allDisplayedChecked) {
        for (const id of contactIdOnPage) next.delete(id);
      } else {
        for (const id of contactIdOnPage) next.add(id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const [createContact, { isLoading: isCreating }] = useCreateContactMutation();
  const [deleteContact] = useDeleteContactMutation();

  /**
   * Central capabilities, never a role. False while the context is still
   * resolving, so the control cannot flash enabled and then retract.
   */
  const { canDeleteContact } = useContactPermissions();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    title: '',
    type: '',
    notes: '',
  });

  /**
   * The CANONICAL companies to link the new contact to, and which is primary.
   *
   * Held as records rather than names because the name is not the identity:
   * posting `company: "Vanjure"` is what produced contacts showing a company
   * that existed nowhere in Companies. The form submits `companyIds` +
   * `primaryCompanyId`; the server links them in the same transaction that
   * creates the contact, and the relationship service then owns the
   * `contacts.company` display value. One writer, one source of truth.
   */
  const [selectedCompanies, setSelectedCompanies] = useState<CompanyChoice[]>([]);
  const [primaryCompanyId, setPrimaryCompanyId] = useState<string | null>(null);
  // Optional per-company role / type / start date, keyed by company id. Lives
  // in a collapsed section; nothing is sent unless something was entered.
  const [companyLinkDetails, setCompanyLinkDetails] = useState<Record<string, CreateLinkDetails>>({});
  const [linkDetailErrors, setLinkDetailErrors] = useState<Record<string, string>>({});

  /**
   * Company text typed into the picker but not resolved to a record.
   *
   * Submitting with this non-empty used to create the contact with NO company
   * while the user watched their typed company sit in the box — measured on
   * staging. Saving the text instead is the orphan string this whole unit
   * removed, and auto-creating the company would exercise `companies.write`
   * from a `contacts.write` form. So the submit is refused and says why, which
   * is the only option that neither lies nor escalates.
   */
  const [pendingCompanyText, setPendingCompanyText] = useState('');

  // Surfaces the backend `message` on a failed create (e.g. the 409
  // CONTACT_ALREADY_EXISTS response). null when there is no active
  // error to render.
  const [createError, setCreateError] = useState<string | null>(null);

  const resetForm = () => {
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      title: '',
      type: '',
      notes: '',
    });
    setSelectedCompanies([]);
    setPrimaryCompanyId(null);
    setCompanyLinkDetails({});
    setLinkDetailErrors({});
    setPendingCompanyText('');
    setCreateError(null);
  };

  const handleCreateContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspaceId) return;

    setCreateError(null); // clear any previous error before retrying
    setLinkDetailErrors({});

    // Refuse rather than silently drop what the user typed. See
    // `pendingCompanyText` — this is the one case where the form knows the
    // user meant something it is not allowed to guess at.
    if (pendingCompanyText.trim()) {
      setCreateError(
        `Select “${pendingCompanyText.trim()}” from the company list, or create it, ` +
          'before saving. Typed text on its own is not saved as a company.',
      );
      return;
    }
    // Which organization a person principally belongs to is the user's call.
    if (selectedCompanies.length > 1 && !primaryCompanyId) {
      setCreateError('Choose which company is primary before saving.');
      return;
    }

    try {
      await createContact({
        workspaceId,
        fullName: formData.fullName,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        // Canonical identities, never display strings. See `selectedCompanies`.
        companyIds: selectedCompanies.map((c) => c.id),
        primaryCompanyId: selectedCompanies.length > 0 ? primaryCompanyId : null,
        companyLinkDetails: compactLinkDetails(
          companyLinkDetails,
          selectedCompanies.map((c) => c.id),
        ),
        title: formData.title || undefined,
        type: formData.type || undefined,
        notes: formData.notes || undefined,
      }).unwrap();
      setShowCreateModal(false);
      resetForm();
    } catch (error: any) {
      // Prefer the backend's human-readable `message` (e.g. "A contact
      // with this email already exists.") over the machine `error` code.
      const fieldErrors = relationshipFieldErrors(error);
      setLinkDetailErrors(fieldErrors);
      const backendMessage =
        Object.keys(fieldErrors).length > 0
          ? 'Some relationship details are invalid. Check the highlighted fields.'
          : error?.data?.message || error?.data?.error || 'Failed to create contact';
      setCreateError(backendMessage);
      // Keep the console diagnostic so the previous debug path is not lost.
      console.error('Failed to create contact:', error);
    }
  };

  const handleDeleteClick = (contact: Contact) => {
    setDeleteError(null);
    setContactToDelete(contact);
  };

  const handleConfirmDelete = async () => {
    if (!contactToDelete) return;
    try {
      await deleteContact(contactToDelete.id).unwrap();
      setContactToDelete(null);
      setDeleteError(null);
    } catch (error) {
      // This used to be `console.error` alone: the dialog closed, the contact
      // stayed in the list, and nothing on screen explained why. The server's
      // own answer is precise — CAPABILITY_DENIED, missing
      // crm:contacts:delete — so say it in words the user can act on, and
      // keep the dialog open so the message has somewhere to live.
      //
      // A control the user cannot use is hidden below, so reaching here means
      // the client's capability map was behind the server's — an admin
      // changing access mid-session, most likely. That is exactly the case
      // that must not fail silently.
      setDeleteError(
        capabilityDeniedMessage(error, 'delete contacts') ??
          'That contact could not be deleted. Please try again.',
      );
      console.error('Failed to delete contact:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ErrorState message="Failed to load contacts." onRetry={refetch} />;
  }

  if (!activeWorkspace) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a workspace to view contacts.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/*
        Header — count and primary action only. The page TITLE now belongs to
        `ContactsHubPage`, which renders "Contacts" once above the People /
        Companies tabs; repeating it here would give the tab body a second
        heading identical to the one directly above it.
      */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500" data-testid="contacts-total">
          {totalContacts.toLocaleString()} contact{totalContacts !== 1 ? 's' : ''}
        </p>
        <Button onClick={() => setShowCreateModal(true)}>Add Contact</Button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search contacts..."
            className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[160px]"
        >
          <option value="">All Types</option>
          {CONTACT_TYPES.map((type) => (
            <option key={type} value={type}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          placeholder="Filter by tags (comma-separated)"
          className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[220px]"
        />
        <ContactCircleFilter
          workspaceId={workspaceId}
          value={circleFilterId}
          onChange={setCircleFilterId}
          className="min-w-[160px]"
        />
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3"
          role="region"
          aria-label="Bulk contact actions"
          data-testid="contacts-bulk-bar"
        >
          <p className="text-sm text-indigo-900">
            {selectedIds.size} contact{selectedIds.size === 1 ? '' : 's'} selected
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowBulkCircleModal(true)}
              className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
              data-testid="contacts-bulk-add-to-circle"
            >
              Add to Circle
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="px-3 py-2 border border-indigo-300 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-100"
            >
              Clear selection
            </button>
          </div>
        </div>
      )}

      {circleActionBanner && (
        <div
          role="status"
          className={`rounded-lg border px-3 py-2 text-sm ${
            circleActionBanner.kind === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
          data-testid="contacts-circle-banner"
        >
          {circleActionBanner.message}
        </div>
      )}

      {/* Contact List */}
      {contacts?.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          {/* A filtered-empty list is not an empty workspace: any active
              search, type, tag or circle filter gets its own message and a
              way back, instead of "No Contacts Yet". */}
          {hasActiveFilters ? (
            <>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No contacts match your filters</h3>
              <p className="text-gray-500 mb-4">Try a different search or clear the filters.</p>
              <Button variant="secondary" onClick={clearFilters} data-testid="contacts-clear-filters">
                Clear filters
              </Button>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No Contacts Yet</h3>
              <p className="text-gray-500 mb-4">Add your first contact to get started.</p>
              <Button onClick={() => setShowCreateModal(true)}>Add Contact</Button>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th scope="col" className="px-4 py-3 w-8">
                    <input
                      type="checkbox"
                      aria-label="Select all displayed contacts"
                      checked={allDisplayedChecked}
                      onChange={toggleSelectAllDisplayed}
                      className="rounded text-blue-600 focus:ring-blue-500"
                      data-testid="contacts-select-all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                    Company
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {contacts?.map((contact) => (
                  <tr
                    key={contact.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedContactId(contact.id)}
                  >
                    <td
                      className="px-4 py-3 w-8"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        aria-label={`Select ${contact.fullName}`}
                        checked={selectedIds.has(contact.id)}
                        onChange={() => toggleSelected(contact.id)}
                        className="rounded text-blue-600 focus:ring-blue-500"
                        data-testid={`contacts-select-${contact.id}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-medium text-blue-600">
                            {contact.fullName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {contact.fullName}
                          </p>
                          {contact.title && (
                            <p className="text-xs text-gray-500 truncate">{contact.title}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {contact.email || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
                      {contact.phone || '-'}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {contact.type && (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full capitalize">
                          {contact.type}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell">
                      {contact.company || '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <ContactQuickActions
                          contactId={contact.id}
                          contactName={contact.fullName}
                          phone={contact.phone}
                        />
                        {/* Hidden rather than disabled: a greyed-out Delete
                            on every row is a permanent reminder of something
                            the user cannot do and will never be able to from
                            here. The server still enforces it — this only
                            decides what is offered. */}
                        {canDeleteContact && (
                          <button
                            data-testid={`contact-delete-${contact.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(contact);
                            }}
                            className="text-red-600 hover:text-red-700 text-sm"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-gray-200 px-4 py-3">
            <ListPager
              total={totalContacts}
              offset={offset}
              count={contacts.length}
              pageSize={CONTACTS_PAGE_SIZE}
              onOffsetChange={setOffset}
              noun={['contact', 'contacts']}
              testId="contacts-pager"
            />
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            ref={createDialogRef}
            data-testid="contact-create-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-create-heading"
            className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6"
          >
            <h2 id="contact-create-heading" className="text-xl font-semibold text-gray-900 mb-4">Add Contact</h2>
            <form onSubmit={handleCreateContact} className="space-y-4">
              <div>
                <label htmlFor="contact-create-fullname" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  id="contact-create-fullname"
                  // Opening the form puts focus in it, so the trap starts
                  // from inside rather than from the button behind it.
                  autoFocus
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label htmlFor="contact-create-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  id="contact-create-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="contact-create-phone" className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  id="contact-create-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Full width: selected companies stack and wrap here. */}
              <CompanyMultiPicker
                value={selectedCompanies}
                primaryId={primaryCompanyId}
                onChange={(companies, primary) => {
                  setSelectedCompanies(companies);
                  setPrimaryCompanyId(primary);
                }}
                onPendingTextChange={setPendingCompanyText}
                disabled={isCreating}
              />
              <LinkDetailsSection
                links={selectedCompanies.map((c) => ({ id: c.id, name: c.name }))}
                value={companyLinkDetails}
                onChange={setCompanyLinkDetails}
                errors={linkDetailErrors}
                disabled={isCreating}
              />

              <div>
                <label htmlFor="contact-create-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  id="contact-create-title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="contact-create-type" className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  id="contact-create-type"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select type...</option>
                  {CONTACT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="contact-create-notes" className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  id="contact-create-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" isLoading={isCreating} className="flex-1">
                  Create
                </Button>
              </div>

              {createError && (
                <p
                  data-testid="contact-create-error"
                  role="alert"
                  className="text-sm text-red-600 mt-2"
                >
                  {createError}
                </p>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Contact Detail SlideOver */}
      {workspaceId && (
        <ContactDetailSlideOver
          contactId={selectedContactId}
          workspaceId={workspaceId}
          onClose={() => {
            setSelectedContactId(null);
            onContactClosed?.();
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={!!contactToDelete}
        error={deleteError}
        itemName={contactToDelete?.fullName}
        itemType="contact"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setContactToDelete(null);
          setDeleteError(null);
        }}
      />

      {/* Add-to-Circle bulk action */}
      {showBulkCircleModal && workspaceId && (
        <ContactBulkAddToCircle
          workspaceId={workspaceId}
          selectedContactIds={Array.from(selectedIds)}
          onClose={() => setShowBulkCircleModal(false)}
          onResult={({ added, skipped, circleName }) => {
            setShowBulkCircleModal(false);
            clearSelection();
            // A Circle filter is server-side now: re-ask so new members show.
            if (circleFilterId) refetch();
            setCircleActionBanner({
              kind: added > 0 ? 'success' : 'error',
              message:
                added > 0
                  ? `Added ${added} contact${added === 1 ? '' : 's'} to ${circleName}${
                      skipped > 0 ? ` (${skipped} skipped)` : ''
                    }.`
                  : 'No contacts were added to the circle.',
            });
          }}
        />
      )}
    </div>
  );
}
